// SCR-STAFF-1008 — جزئیات ارسال و لاگ ماسک‌شده یک موجودیت (AP-INT-09؛ US-INT-110).
//
// همه تلاش‌ها با زمان، عملیات، کد نتیجه، کد نگاشت‌شده `PHX-*`، متن اصلی خطای بیمه‌گر و بدنه **Redact‌شده** درخواست و
// پاسخ. دارنده `int.log.view_own` فقط فراخوان‌های آغازشده توسط خودش را می‌بیند (BR-INT-103) و هر مشاهده در Audit
// می‌نشیند. هیچ اعتبارنامه، OTP یا کد ملی کاملی در بدنه‌ها نیست (BR-INT-030).
import ExpandMoreOutlinedIcon from '@mui/icons-material/ExpandMoreOutlined';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Alert from '@mui/material/Alert';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import { unwrap } from '@phoenix/api-client';
import { useTenantSession } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import { ErrorState, faDigits, JalaliDate, ListPage, LoadingState, SampleBadge } from '@phoenix/ui';
import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router';
import { intKeys } from './common';

export const SCR_STAFF_1008 = 'SCR-STAFF-1008';

export function EntityCallLogPage() {
  const { t } = useTranslation('int');
  const { tenantApi, session } = useTenantSession();
  const [params] = useSearchParams();
  const consumerRef = params.get('consumerRef') ?? '';

  const calls = useQuery({
    queryKey: intKeys.entityCalls(session?.tenantId, consumerRef),
    enabled: consumerRef !== '',
    queryFn: () =>
      unwrap(
        tenantApi.GET('/api/v1/integration/entity-calls', {
          params: { query: { consumerRef } },
        }),
      ),
  });

  if (consumerRef === '') {
    return (
      <ListPage screenId={SCR_STAFF_1008} titleKey="int:entity_calls.title" subtitleKey="int:entity_calls.subtitle">
        <Alert severity="info" data-testid="entity-calls-empty">
          {t('int:entity_calls.no_ref')}
        </Alert>
      </ListPage>
    );
  }

  if (calls.isPending) {
    return <LoadingState />;
  }

  if (calls.error) {
    return <ErrorState error={calls.error} onRetry={() => void calls.refetch()} />;
  }

  const items = calls.data?.items ?? [];

  return (
    <ListPage
      screenId={SCR_STAFF_1008}
      titleKey="int:entity_calls.title"
      subtitleKey="int:entity_calls.subtitle"
      filters={
        <Typography variant="body2" color="text.secondary" data-testid="entity-calls-ref" dir="ltr">
          {consumerRef}
        </Typography>
      }
    >
      {items.length === 0 ? (
        <Alert severity="info" data-testid="entity-calls-empty">
          {t('int:entity_calls.empty')}
        </Alert>
      ) : (
        <Stack spacing={1} data-testid="entity-calls-table">
          {items.map((call) => (
            <Accordion key={call.callId} disableGutters data-testid={`entity-call-${call.callId}`}>
              <AccordionSummary expandIcon={<ExpandMoreOutlinedIcon />}>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                  <Typography variant="body2">
                    <JalaliDate value={call.createdAt} withTime />
                  </Typography>
                  <Typography variant="body2" dir="ltr">
                    {call.operation}
                  </Typography>
                  <Typography variant="body2" color={call.resultCode === 'OK' ? 'success.main' : 'error.main'} dir="ltr">
                    {call.resultCode ?? '—'}
                  </Typography>
                  {call.actionType ? (
                    <Typography variant="caption" color="text.secondary">
                      {t(`int:entity_calls.action.${call.actionType}`, { defaultValue: call.actionType })}
                    </Typography>
                  ) : null}
                  {call.isSample ? (
                    <span data-testid={`entity-call-sample-${call.callId}`}>
                      <SampleBadge />
                    </span>
                  ) : null}
                </Stack>
              </AccordionSummary>
              <AccordionDetails>
                <Stack spacing={1}>
                  {call.providerMessage ? (
                    <Alert severity="warning" data-testid={`entity-call-provider-message-${call.callId}`}>
                      {call.providerMessage}
                    </Alert>
                  ) : null}
                  <Table size="small" data-testid={`entity-call-attempts-${call.callId}`}>
                    <TableHead>
                      <TableRow>
                        <TableCell>{t('int:entity_calls.attempt')}</TableCell>
                        <TableCell>{t('int:entity_calls.started_at')}</TableCell>
                        <TableCell>{t('int:entity_calls.outcome')}</TableCell>
                        <TableCell>{t('int:entity_calls.error')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(call.tries ?? []).map((attempt) => (
                        <TableRow key={attempt.attempt}>
                          <TableCell>{faDigits(attempt.attempt)}</TableCell>
                          <TableCell>
                            <JalaliDate value={attempt.startedAt} withTime />
                          </TableCell>
                          <TableCell dir="ltr">{attempt.outcome}</TableCell>
                          <TableCell dir="ltr">{attempt.errorCode ?? '—'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  {(call.tries ?? []).map((attempt) =>
                    attempt.requestJson || attempt.responseJson ? (
                      <Stack key={`body-${attempt.attempt}`} spacing={0.5}>
                        <Typography variant="caption" color="text.secondary">
                          {t('int:entity_calls.masked_bodies')}
                        </Typography>
                        <Typography variant="caption" component="pre" dir="ltr" sx={{ whiteSpace: 'pre-wrap', m: 0 }}>
                          {attempt.requestJson ?? ''}
                        </Typography>
                        <Typography variant="caption" component="pre" dir="ltr" sx={{ whiteSpace: 'pre-wrap', m: 0 }}>
                          {attempt.responseJson ?? ''}
                        </Typography>
                      </Stack>
                    ) : null,
                  )}
                </Stack>
              </AccordionDetails>
            </Accordion>
          ))}
        </Stack>
      )}
    </ListPage>
  );
}
